import { AnchorButton, Spinner } from '@birdi/components-common';
import BirdiLogoNavySVG from '@birdi/icons/birdi-logo-navy.svg';
import MapAccessLockSVG from '@birdi/icons/map-access-lock.svg';
import { css, useTheme } from '@emotion/react';
import React from 'react';

const loadFailMessages: Record<
  string,
  { title: string; message: React.ReactNode }
> = {
  '403': {
    title: "You don't have permission to view this map",
    message: 'Contact the person who shared it with you to request access.',
  },
  '404': {
    title: 'Not Found',
    message: "The map you're trying to find does not seem to exist.",
  },
  default: {
    title: 'Something Broke!',
    message: (
      <>
        We had problems trying to load that map. Try refreshing the page, or
        contact <a href="mailto:enquiries@birdi.io">enquiries@birdi.io</a> if it
        persists.
      </>
    ),
  },
};

export const LoadFailScreen = ({ reasonCode }: { reasonCode: number }) => {
  const reason = loadFailMessages[reasonCode] || loadFailMessages.default;
  // const reason = loadFailMessages['403'];

  return (
    <div
      css={(theme) => css`
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        background: ${theme.sand};
        display: flex;
        flex-direction: column;
      `}
    >
      <header
        css={(theme) => css`
          display: flex;
          align-items: center;
          height: 60px;
          width: 100%;
          background: ${theme.dove};
          padding-inline: 1rem;
          box-sizing: border-box;
        `}
      >
        <BirdiLogoNavySVG
          css={css`
            font-size: 3em;
          `}
        />
      </header>

      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
        `}
      >
        <main
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            background: #fff;
            border-radius: 20px;
            padding: 2rem;
            gap: 1rem;
            box-sizing: border-box;
            max-width: 500px;
            width: 100%;
          `}
        >
          <header
            css={(theme) => css`
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 1ch;
              border-bottom: solid 2px ${theme.sand};
              padding-bottom: 1rem;
              font-size: 24px;
              width: 100%;
            `}
          >
            <MapAccessLockSVG width="40px" height="40px" />
            <BirdiLogoNavySVG
              width="55px"
              height="auto"
              css={css`
                bottom: 12px;
                position: relative;
              `}
            />
            Map
          </header>

          <h1
            css={css`
              margin: 0;
              font-size: 20px;
              font-weight: 600;
            `}
          >
            {reason.title}
          </h1>
          <p
            css={css`
              margin: 0;
              margin-bottom: 0.5rem;
              max-width: 350px;
            `}
          >
            {reason.message}
          </p>

          <AnchorButton
            href="/"
            css={css`
              align-self: center;
            `}
          >
            Return to Dashboard
          </AnchorButton>
          {/* TODO: logged in as info? */}
        </main>
      </div>
    </div>
  );
};

export const LoadingScreen = () => {
  const theme = useTheme();

  return (
    <div
      css={(theme) => css`
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        background: ${theme.sand};
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 1rem;
        box-sizing: border-box;
        text-align: center;
        gap: 0.5rem;
        z-index: 9999;
      `}
    >
      <BirdiLogoNavySVG
        css={css`
          font-size: 3em;
          position: absolute;
          top: 1rem;
          left: 1rem;
        `}
      />
      <Spinner size="2rem" color={theme.deepOcean} />
    </div>
  );
};
